// src/pages/Tools/nintendo-quest/StreakCards.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Flame, 
  BookOpen, 
  Globe, 
  Calculator, 
  Book, 
  Dumbbell 
} from 'lucide-react';
import { Streaks } from '../../../lib/nintendo-quest/types';

interface StreakCardsProps {
  streaks: Streaks;
}

const streakConfig = [
  {
    key: 'dailyYoga' as keyof Streaks,
    label: 'Daily Yoga',
    icon: Dumbbell,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-400/30'
  },
  {
    key: 'sanskritDaily' as keyof Streaks,
    label: 'Sanskrit',
    icon: BookOpen,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-400/30'
  },
  {
    key: 'gujaratiSpeaking' as keyof Streaks,
    label: 'Gujarati',
    icon: Globe,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-400/30'
  },
  {
    key: 'ixlDaily' as keyof Streaks,
    label: 'IXL Math',
    icon: Calculator,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-400/30'
  },
  {
    key: 'readingDaily' as keyof Streaks,
    label: 'Reading',
    icon: Book,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-400/30'
  },
  {
    key: 'perfectDays' as keyof Streaks,
    label: 'Perfect Days',
    icon: Flame,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-400/30'
  }
];

export default function StreakCards({ streaks }: StreakCardsProps) {
  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Flame className="w-6 h-6 text-orange-400" />
          Current Streaks
        </h3>
        <div className="text-sm text-white/60">
          Keep it going! 🔥
        </div>
      </div>

      {/* Streak Cards */}
      <div className="space-y-3">
        {streakConfig.map((config, index) => {
          const streak = streaks[config.key];
          const Icon = config.icon;
          
          return (
            <motion.div
              key={config.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  <div>
                    <div className="text-white font-medium">{config.label}</div>
                    <div className="text-white/60 text-sm">
                      Current: {streak.current} days
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-white">
                    {streak.current}
                  </div>
                  <div className="text-xs text-white/50">
                    Best: {streak.best}
                  </div>
                </div>
              </div>
              
              {/* Progress bar showing current vs best */}
              <div className="mt-3">
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className={`h-full ${config.color.replace('text-', 'bg-').replace('-400', '-500')} rounded-full transition-all duration-300`}
                    style={{ width: `${Math.min((streak.current / streak.best) * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Streak status */}
              {streak.current === streak.best && streak.current > 0 && (
                <div className="mt-2 text-xs text-yellow-300 font-medium">
                  🏆 Personal Best!
                </div>
              )}
              {streak.current > 7 && (
                <div className="mt-2 text-xs text-orange-300 font-medium">
                  🔥 On Fire!
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}